import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { getHabboImageUrl } from './HabboImage';

const CENTER_RADIUS = 58;
const RIM_WIDTH = 7;
const POINTER_H = 48;
const POINTER_W = 32;
const BG_CIRCLE_R = 36;
const IMG_FRAC = 0.55;
const CANVAS_SIZE = 600;
const IDLE_SPEED = 0.12; // rad/s — gentle ambient drift (~1.1 RPM)
const IDLE_FRAME_MS = 1000 / 30; // idle + glow target 30 fps — smooth enough, saves GPU

function easeSpinOut(t) { return 1 - Math.pow(1 - t, 3); } // cubic: fast start, sustained speed, gradual stop

function getSegmentImageSrcs(seg) {
    if (!seg) return [];
    if (seg.type === 'habbo') {
        const src = getHabboImageUrl({ habbo: seg.habboName, size: 's', direction: 2, head_direction: 2, shadow: false });
        return src ? [src] : [];
    }
    if (seg.type === 'furni' && seg.furnis?.length) {
        return seg.furnis.map(f => {
            const code = String(f.code || '').replace(/%2a/gi, '*');
            const star = code.indexOf('*');
            const base = star === -1 ? code : code.slice(0, star);
            const rest = star === -1 ? '' : code.slice(star);
            return `https://furni.franklyorigins.net/api/furniture/render?furni=${encodeURIComponent(`${base}_small${rest}`)}&direction=0&state=1&frame=0&width=120&height=120`;
        });
    }
    if (seg.type === 'custom' && seg.iconNum) {
        return [`${process.env.PUBLIC_URL}/img/icons/icon_${seg.iconNum}.png`];
    }
    return [];
}

function getFurniFallbackSrc(code) {
    const c = String(code || '').replace(/%2a/gi, '*');
    return `https://furni.franklyorigins.net/api/furniture/render?furni=${encodeURIComponent(c)}&direction=0&state=0&frame=0&width=120&height=120`;
}

const imgKey = (segId, idx) => `${segId}_${idx}`;
const bgKey = segId => `${segId}_bg`;

const WheelCanvas = forwardRef(function WheelCanvas({
    segments,
    colors,
    fontValue,
    spinDuration = 6000,
    textSizeMultiplier = 1.35,
    textOrientation = 'radial',
    onWinnerPicked,
}, ref) {
    const canvasRef = useRef(null);
    const rotRef = useRef(0);
    const rafRef = useRef(null);
    const spinningRef = useRef(false);
    const imagesRef = useRef({});
    const centerImgRef = useRef(null);
    const segmentsRef = useRef(segments);
    const colorsRef = useRef(colors);
    const fontRef = useRef(fontValue);
    const spinDurRef = useRef(spinDuration);
    const textSizeRef = useRef(textSizeMultiplier);
    const textOrientRef = useRef(textOrientation);
    const winnerIdxRef = useRef(null);
    const glowRafRef = useRef(null);
    const dprRef = useRef(1);
    const idleActiveRef = useRef(false);
    const idleRafRef = useRef(null);
    const spinStartTimeRef = useRef(0);

    useEffect(() => { segmentsRef.current = segments; }, [segments]);
    useEffect(() => { colorsRef.current = colors; }, [colors]);
    useEffect(() => { fontRef.current = fontValue; }, [fontValue]);
    useEffect(() => { spinDurRef.current = spinDuration; }, [spinDuration]);
    useEffect(() => { textSizeRef.current = textSizeMultiplier; }, [textSizeMultiplier]);
    useEffect(() => { textOrientRef.current = textOrientation; }, [textOrientation]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const dpr = window.devicePixelRatio || 1;
        dprRef.current = dpr;
        canvas.width = CANVAS_SIZE * dpr;
        canvas.height = CANVAS_SIZE * dpr;
        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);
        drawFrame();
    }, []); // eslint-disable-line

    // Load centre image
    useEffect(() => {
        const img = new Image();
        img.onload = () => { centerImgRef.current = img; drawFrame(); };
        img.src = `${process.env.PUBLIC_URL}/img/franklyshrug.png`;
    }, []); // eslint-disable-line

    // Load all segment images + habbo bg images
    useEffect(() => {
        const newMap = {};

        function loadImg(key, src, fallbackSrc) {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => { imagesRef.current = { ...imagesRef.current, [key]: img }; drawFrame(); };
            img.onerror = () => {
                if (fallbackSrc) {
                    const img2 = new Image();
                    img2.crossOrigin = 'anonymous';
                    img2.onload = () => { imagesRef.current = { ...imagesRef.current, [key]: img2 }; drawFrame(); };
                    img2.src = fallbackSrc;
                }
            };
            img.src = src;
            newMap[key] = null;
        }

        for (const seg of segments) {
            const srcs = getSegmentImageSrcs(seg);
            srcs.forEach((src, idx) => {
                const fallback = seg.type === 'furni' ? getFurniFallbackSrc(seg.furnis[idx]?.code) : null;
                loadImg(imgKey(seg.id, idx), src, fallback);
            });
            // Habbo bg when bgColor is a URL (not hex)
            if (seg.type === 'habbo' && seg.bgColor && !seg.bgColor.startsWith('#')) {
                loadImg(bgKey(seg.id), seg.bgColor, null);
            }
        }

        imagesRef.current = newMap;
        drawFrame();
    }, [segments]); // eslint-disable-line

    const drawFrame = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        const segs = segmentsRef.current;
        const cols = colorsRef.current;
        const font = fontRef.current || "'Jersey 10', sans-serif";
        const rot = rotRef.current;
        const imgs = imagesRef.current;
        const centerImg = centerImgRef.current;
        const tsMul = textSizeRef.current;
        const textOrient = textOrientRef.current;

        const W = CANVAS_SIZE;
        const H = CANVAS_SIZE;
        const cx = W / 2;
        const cy = H / 2;
        const R = Math.min(cx, cy) - POINTER_H - 8;
        const n = segs.length;

        ctx.clearRect(0, 0, W, H);

        if (n === 0) {
            _drawEmptyWheel(ctx, cx, cy, R);
            _drawPointer(ctx, cx, cy, R);
            return;
        }

        const arcSize = (2 * Math.PI) / n;
        const startOff = -Math.PI / 2;
        const imgR = CENTER_RADIUS + (R - CENTER_RADIUS) * IMG_FRAC;

        // ── Segments ──
        for (let i = 0; i < n; i++) {
            const a0 = startOff + rot + i * arcSize;
            const a1 = a0 + arcSize;
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.arc(cx, cy, R, a0, a1);
            ctx.closePath();
            ctx.fillStyle = cols[i % cols.length] || '#555555';
            ctx.fill();
            ctx.restore();
        }

        // ── Depth gradient ──
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, R, 0, Math.PI * 2);
        ctx.clip();
        const depthGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
        depthGrad.addColorStop(0, 'rgba(255,255,255,0.18)');
        depthGrad.addColorStop(0.55, 'rgba(255,255,255,0)');
        depthGrad.addColorStop(1, 'rgba(0,0,0,0.22)');
        ctx.fillStyle = depthGrad;
        ctx.fillRect(0, 0, W, H);
        ctx.restore();

        // ── Specular ──
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, R - RIM_WIDTH - 4, -Math.PI * 0.78, -Math.PI * 0.22);
        const shineGrad = ctx.createLinearGradient(cx - R * 0.4, cy - R, cx + R * 0.4, cy - R * 0.3);
        shineGrad.addColorStop(0, 'rgba(255,255,255,0)');
        shineGrad.addColorStop(0.5, 'rgba(255,255,255,0.13)');
        shineGrad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.strokeStyle = shineGrad;
        ctx.lineWidth = R * 0.14;
        ctx.stroke();
        ctx.restore();

        // ── Dividers ──
        for (let i = 0; i < n; i++) {
            const angle = startOff + rot + i * arcSize;
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(cx + Math.cos(angle) * (R - RIM_WIDTH), cy + Math.sin(angle) * (R - RIM_WIDTH));
            ctx.strokeStyle = 'rgba(0,0,0,0.3)';
            ctx.lineWidth = 1.5;
            ctx.stroke();
            ctx.restore();
        }

        // ── Images ──
        for (let i = 0; i < n; i++) {
            const a0 = startOff + rot + i * arcSize;
            const a1 = a0 + arcSize;
            const mid = a0 + arcSize / 2;
            const seg = segs[i];
            const px = cx + Math.cos(mid) * imgR;
            const py = cy + Math.sin(mid) * imgR;

            // Clip to pie slice
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.arc(cx, cy, R - RIM_WIDTH - 1, a0, a1);
            ctx.closePath();
            ctx.clip();

            if (seg.type === 'habbo') {
                const bgIsHex = seg.bgColor && seg.bgColor.startsWith('#');
                const bgImgLoaded = imgs[bgKey(seg.id)];
                const avatarImg = imgs[imgKey(seg.id, 0)];

                ctx.save();
                ctx.translate(px, py);
                ctx.rotate(mid + Math.PI / 2);

                // 1. Background fill (hex colour or loaded bg image)
                ctx.save();
                ctx.beginPath();
                ctx.arc(0, 0, BG_CIRCLE_R, 0, Math.PI * 2);
                if (bgIsHex) {
                    ctx.fillStyle = seg.bgColor;
                    ctx.fill();
                } else if (bgImgLoaded && bgImgLoaded.complete && bgImgLoaded.naturalWidth > 0) {
                    ctx.clip();
                    const scale = (BG_CIRCLE_R * 2) / Math.min(bgImgLoaded.naturalWidth, bgImgLoaded.naturalHeight);
                    const dw = bgImgLoaded.naturalWidth * scale;
                    const dh = bgImgLoaded.naturalHeight * scale;
                    ctx.drawImage(bgImgLoaded, -dw / 2, -dh / 2, dw, dh);
                } else {
                    ctx.fillStyle = '#5A2929';
                    ctx.fill();
                }
                ctx.restore();

                // 2. Circle border
                ctx.save();
                ctx.beginPath();
                ctx.arc(0, 0, BG_CIRCLE_R, 0, Math.PI * 2);
                ctx.strokeStyle = 'rgba(0,0,0,0.3)';
                ctx.lineWidth = 1.5;
                ctx.stroke();
                ctx.restore();

                // 3. Avatar clipped to circle (same rotated ctx, no extra rotation)
                if (avatarImg && avatarImg.complete && avatarImg.naturalWidth > 0) {
                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(0, 0, BG_CIRCLE_R, 0, Math.PI * 2);
                    ctx.clip();
                    ctx.drawImage(avatarImg, -avatarImg.naturalWidth / 2, -avatarImg.naturalHeight / 2);
                    ctx.restore();
                }

                ctx.restore(); // pop translate+rotate

            } else if (seg.type === 'furni' && seg.furnis?.length) {
                const furniList = seg.furnis;
                const showCount = Math.min(furniList.length, 4);
                // Target display size per item (natural-size capped, not stretched)
                const maxItemSize = showCount === 1 ? 60 : showCount === 2 ? 50 : 40;
                const gap = 4;

                // Compute actual display dimensions for each item (contain scaling)
                const itemDims = [];
                for (let fi = 0; fi < showCount; fi++) {
                    const fImg = imgs[imgKey(seg.id, fi)];
                    if (fImg && fImg.complete && fImg.naturalWidth > 0) {
                        const scale = Math.min(1, maxItemSize / Math.max(fImg.naturalWidth, fImg.naturalHeight));
                        itemDims.push({ w: fImg.naturalWidth * scale, h: fImg.naturalHeight * scale, img: fImg });
                    } else {
                        itemDims.push({ w: 0, h: 0, img: null });
                    }
                }

                const totalW = itemDims.reduce((s, d) => s + d.w, 0) + (showCount - 1) * gap;
                // Slight tilt offsets for playful look
                const tilts = [0, -0.09, 0.11, -0.06];

                ctx.save();
                ctx.translate(px, py);
                ctx.rotate(mid + Math.PI / 2); // bottom faces centre

                let xCursor = -totalW / 2;
                for (let fi = 0; fi < showCount; fi++) {
                    const { w, h, img: fImg } = itemDims[fi];
                    const fItem = furniList[fi];
                    const xCenter = xCursor + w / 2;

                    if (fImg) {
                        ctx.save();
                        ctx.translate(xCenter, 0);
                        ctx.rotate(tilts[fi] || 0);
                        // Draw at computed size — proportional, no stretching
                        ctx.drawImage(fImg, -w / 2, -h / 2, w, h);
                        ctx.restore();
                    }

                    // x[qty] label — drawn without any shadow to stay crisp
                    if (fItem.qty > 1) {
                        ctx.save();
                        // No shadow, no synthetic bold, no stroke — just a clean fill
                        ctx.shadowColor = 'transparent';
                        ctx.shadowBlur = 0;
                        ctx.shadowOffsetX = 0;
                        ctx.shadowOffsetY = 0;
                        ctx.font = "9px VolterBoldGoldfish, sans-serif";
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'top';
                        const qtxt = `x${fItem.qty}`;
                        const qy = h / 2 + 1;
                        ctx.fillStyle = '#ffffff';
                        ctx.fillText(qtxt, xCenter, qy);
                        ctx.restore();
                    }

                    xCursor += w + gap;
                }
                ctx.restore();

            } else if (seg.type === 'custom') {
                const customImg = imgs[imgKey(seg.id, 0)];
                if (customImg && customImg.complete && customImg.naturalWidth > 0) {
                    ctx.save();
                    ctx.translate(px, py);
                    ctx.rotate(mid + Math.PI / 2);
                    ctx.drawImage(customImg, -customImg.naturalWidth / 2, -customImg.naturalHeight / 2);
                    ctx.restore();
                }
            }

            ctx.restore(); // pop pie-slice clip
        }

        // ── Text ──
        const innerTextStart = CENTER_RADIUS + 10;
        const innerTextEnd = imgR - BG_CIRCLE_R - 8;
        const tanTextR = R - RIM_WIDTH - 22;

        for (let i = 0; i < n; i++) {
            const a0 = startOff + rot + i * arcSize;
            const mid = a0 + arcSize / 2;
            const arcH = arcSize * R;

            const isVolter = font.includes('Volter');
            const isJersey = font.includes('Jersey');
            const targetFs = isVolter ? 9 : Math.round(Math.max(8, Math.min(12, Math.floor(arcH * 0.14))) * tsMul);
            const minFs = isVolter ? 9 : 8;
            const raw = segs[i].label || segs[i].habboName || segs[i].text || `#${i + 1}`;

            ctx.save();
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;

            if (textOrient === 'horizontal') {
                const tx = cx + Math.cos(mid) * tanTextR;
                const ty = cy + Math.sin(mid) * tanTextR;
                const maxW = arcSize * tanTextR * 0.80;

                let fs = targetFs;
                ctx.font = `${fs}px ${font}`;
                while (fs > minFs && ctx.measureText(raw).width > maxW) { fs--; ctx.font = `${fs}px ${font}`; }
                let txt = raw;
                if (ctx.measureText(txt).width > maxW) {
                    while (txt.length > 1 && ctx.measureText(txt).width > maxW) txt = txt.slice(0, -1);
                    if (txt.length < raw.length) txt = txt.slice(0, -1) + '…';
                }

                ctx.translate(tx, ty);
                ctx.rotate(mid + Math.PI / 2);
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.lineWidth = 3;
                ctx.strokeStyle = 'rgba(255,255,255,0.75)';
                ctx.strokeText(txt, 0, 0);
                ctx.fillStyle = '#1a1a1a';
                ctx.fillText(txt, 0, 0);

            } else {
                const maxW = Math.max(10, innerTextEnd - innerTextStart);

                let fs = targetFs;
                ctx.font = `${fs}px ${font}`;
                while (fs > minFs && ctx.measureText(raw).width > maxW) { fs--; ctx.font = `${fs}px ${font}`; }
                let txt = raw;
                if (ctx.measureText(txt).width > maxW) {
                    while (txt.length > 1 && ctx.measureText(txt).width > maxW) txt = txt.slice(0, -1);
                    if (txt.length < raw.length) txt = txt.slice(0, -1) + '…';
                }

                ctx.translate(cx, cy);
                ctx.rotate(mid);
                ctx.textAlign = 'left';
                ctx.textBaseline = 'middle';
                ctx.lineWidth = 3;
                ctx.strokeStyle = 'rgba(255,255,255,0.75)';
                ctx.strokeText(txt, innerTextStart, 0);
                ctx.fillStyle = '#1a1a1a';
                ctx.fillText(txt, innerTextStart, 0);
            }

            ctx.restore();
        }

        // ── Winner glow ──
        const wi = winnerIdxRef.current;
        if (wi !== null && n > 0) {
            const pulse = 0.5 + 0.5 * Math.sin(performance.now() / 400);
            const ga0 = startOff + rot + wi * arcSize;
            const ga1 = ga0 + arcSize;
            ctx.save();
            // Pulsing fill tint
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.arc(cx, cy, R - RIM_WIDTH - 1, ga0, ga1);
            ctx.closePath();
            ctx.fillStyle = `rgba(255,230,60,${0.22 * pulse})`;
            ctx.fill();
            // Border stroke — dark shadow ensures visibility on any segment colour
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.arc(cx, cy, R - RIM_WIDTH - 1, ga0, ga1);
            ctx.closePath();
            ctx.shadowColor = 'rgba(0,0,0,0.9)';
            ctx.shadowBlur = 14;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
            ctx.strokeStyle = `rgba(255,230,60,${0.6 + 0.4 * pulse})`;
            ctx.lineWidth = 5;
            ctx.stroke();
            ctx.restore();
        }

        // ── Tick marks ──
        for (let i = 0; i < n; i++) {
            const angle = startOff + rot + i * arcSize;
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(angle);
            ctx.beginPath();
            ctx.moveTo(R - RIM_WIDTH - 2, 0);
            ctx.lineTo(R + 1, 0);
            ctx.strokeStyle = 'rgba(0,0,0,0.55)';
            ctx.lineWidth = 2;
            ctx.stroke();
            ctx.restore();
        }

        // ── Outer rim ──
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, R, 0, Math.PI * 2);
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = RIM_WIDTH + 2;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(cx, cy, R - RIM_WIDTH / 2, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(210,170,50,0.55)';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(cx, cy, R - RIM_WIDTH - 2, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255,255,255,0.18)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.restore();

        // ── Centre shadow ──
        ctx.save();
        const shadowGrad = ctx.createRadialGradient(cx, cy, CENTER_RADIUS - 2, cx, cy, CENTER_RADIUS + 8);
        shadowGrad.addColorStop(0, 'rgba(0,0,0,0.35)');
        shadowGrad.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = shadowGrad;
        ctx.beginPath();
        ctx.arc(cx, cy, CENTER_RADIUS + 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // ── Centre disc ──
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, CENTER_RADIUS, 0, Math.PI * 2);
        const discGrad = ctx.createRadialGradient(cx - CENTER_RADIUS * 0.25, cy - CENTER_RADIUS * 0.25, 2, cx, cy, CENTER_RADIUS);
        discGrad.addColorStop(0, '#ffffff');
        discGrad.addColorStop(0.45, '#f0ede8');
        discGrad.addColorStop(1, '#d4cfc7');
        ctx.fillStyle = discGrad;
        ctx.fill();
        if (centerImg && centerImg.complete && centerImg.naturalWidth > 0) {
            ctx.clip();
            ctx.imageSmoothingEnabled = false;
            const dw = centerImg.naturalWidth / 2;
            const dh = centerImg.naturalHeight / 2;
            ctx.drawImage(centerImg, cx - dw / 2, cy - dh / 2, dw, dh);
            ctx.imageSmoothingEnabled = true;
        }
        ctx.restore();

        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, CENTER_RADIUS, 0, Math.PI * 2);
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(cx, cy, CENTER_RADIUS - 2, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(210,170,50,0.6)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.restore();

        _drawPointer(ctx, cx, cy, R);
    }, []);

    const stopIdle = useCallback(() => {
        idleActiveRef.current = false;
        if (idleRafRef.current) { cancelAnimationFrame(idleRafRef.current); idleRafRef.current = null; }
    }, []);

    const startIdle = useCallback(() => {
        if (idleActiveRef.current) return;
        idleActiveRef.current = true;
        let lastTime = 0;
        function loop(now) {
            if (!idleActiveRef.current) return;
            idleRafRef.current = requestAnimationFrame(loop);
            const elapsed = now - lastTime;
            if (elapsed < IDLE_FRAME_MS) return; // throttle to ~30 fps
            const dt = Math.min(elapsed / 1000, 0.1);
            lastTime = now - (elapsed % IDLE_FRAME_MS); // stay drift-free
            rotRef.current = (rotRef.current + IDLE_SPEED * dt) % (2 * Math.PI);
            drawFrame();
        }
        idleRafRef.current = requestAnimationFrame(loop);
    }, [drawFrame]);

    // Auto-start idle on mount
    useEffect(() => {
        startIdle();
        return () => stopIdle();
    }, [startIdle, stopIdle]);

    function _drawEmptyWheel(ctx, cx, cy, R) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, R, 0, Math.PI * 2);
        ctx.fillStyle = '#2a2a2a';
        ctx.fill();
        ctx.strokeStyle = '#1a1a1a';
        ctx.lineWidth = RIM_WIDTH + 2;
        ctx.stroke();
        ctx.restore();
        ctx.save();
        ctx.font = "bold 14px 'Jersey 10', sans-serif";
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.fillText('Add segments to spin!', cx, cy);
        ctx.restore();
    }

    function _drawPointer(ctx, cx, cy, R) {
        const tipY = cy - R + RIM_WIDTH / 2 + 30;
        const baseY = tipY - POINTER_H;
        const hw = POINTER_W / 2;
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 6;
        ctx.shadowOffsetY = 2;
        ctx.beginPath();
        ctx.moveTo(cx, tipY);
        ctx.lineTo(cx - hw, baseY);
        ctx.lineTo(cx + hw, baseY);
        ctx.closePath();
        ctx.fillStyle = '#1a1a1a';
        ctx.fill();
        ctx.restore();
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(cx, tipY);
        ctx.lineTo(cx - hw, baseY);
        ctx.lineTo(cx + hw, baseY);
        ctx.closePath();
        ctx.strokeStyle = 'rgba(210,170,50,0.8)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.restore();
    }

    const spin = useCallback(({ winnerIndex, offsetFraction, extraTurnCount, startRot: syncRot, totalDelta: providedDelta }) => {
        if (spinningRef.current) return;
        const segs = segmentsRef.current;
        const n = segs.length;
        if (n < 2) return;

        // Stop idle — the current rotation is our random start position
        stopIdle();

        spinningRef.current = true;
        winnerIdxRef.current = null;
        if (glowRafRef.current) { cancelAnimationFrame(glowRafRef.current); glowRafRef.current = null; }

        // If syncRot is provided (from BC message), sync this canvas to the sender's rotation
        if (syncRot !== undefined) rotRef.current = syncRot;

        const arcSize = (2 * Math.PI) / n;
        const offset = arcSize * (0.1 + (offsetFraction ?? 0.5) * 0.8);
        const wantedRot = -(winnerIndex * arcSize + offset);
        const currRot = rotRef.current;
        const turns = (extraTurnCount ?? 10) * 2 * Math.PI;
        let diff = ((wantedRot - currRot) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
        if (diff < 0.05) diff += 2 * Math.PI;
        // Use pre-calculated totalDelta if provided (fixes segment-count race condition on overlay)
        const totalDelta = providedDelta ?? (diff + turns);
        const startRot = currRot;
        const startTime = performance.now();
        spinStartTimeRef.current = startTime;
        const dur = spinDurRef.current;

        function frame(now) {
            const t = Math.min(1, (now - startTime) / dur);
            rotRef.current = startRot + easeSpinOut(t) * totalDelta;
            drawFrame();
            if (t < 1) {
                rafRef.current = requestAnimationFrame(frame);
            } else {
                rotRef.current = startRot + totalDelta;
                // Normalise to prevent float drift on repeated spins
                rotRef.current = ((rotRef.current % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
                const ptr = ((-rotRef.current % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
                const computed = Math.floor(ptr / arcSize) % n;
                spinningRef.current = false;
                winnerIdxRef.current = computed;
                // Start pulsing glow loop at 30 fps — wheel stays completely still here
                let lastGlowTime = 0;
                const glowLoop = (now) => {
                    if (winnerIdxRef.current === null) { glowRafRef.current = null; return; }
                    glowRafRef.current = requestAnimationFrame(glowLoop);
                    if (now - lastGlowTime < IDLE_FRAME_MS) return;
                    lastGlowTime = now;
                    drawFrame();
                };
                glowRafRef.current = requestAnimationFrame(glowLoop);
                drawFrame();
                onWinnerPicked?.(computed, segs[computed]);
            }
        }

        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(frame);
    }, [drawFrame, onWinnerPicked, stopIdle]);

    useEffect(() => { drawFrame(); }, [segments, colors, fontValue, textSizeMultiplier, textOrientation, drawFrame]);

    useImperativeHandle(ref, () => ({
        spin,
        isSpinning: () => spinningRef.current,
        getRotation: () => rotRef.current,
        getSpinStartTime: () => spinStartTimeRef.current,
        clearWinner: () => {
            winnerIdxRef.current = null;
            if (glowRafRef.current) { cancelAnimationFrame(glowRafRef.current); glowRafRef.current = null; }
            drawFrame(); // immediately clear the glow
            startIdle(); // resume slow ambient rotation
        },
        startIdle,
        stopIdle,
        // Set idle rotation directly — called on initial handshake so overlay starts aligned
        syncIdleRotation: (r) => {
            if (!spinningRef.current) {
                rotRef.current = r;
                drawFrame();
            }
        },
        // Adjust the overlay's spin clock so it stays frame-perfect with the main page.
        // Called with the elapsed ms the main page reports; we shift our startTime to match.
        syncSpinClock: (elapsed) => {
            if (!spinningRef.current) return;
            spinStartTimeRef.current = performance.now() - elapsed;
        },
    }), [spin, drawFrame, startIdle, stopIdle]);

    return (
        <div className="relative w-full" style={{ aspectRatio: '1 / 1' }}>
            <canvas ref={canvasRef} style={{
                display: 'block',
                width: '100%',
                height: '100%',
                filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.55))',
            }} />
        </div>
    );
});

export default WheelCanvas;
